import { mapMode } from "../shared/map-mode.js";
import { escape as e } from "./api.js";
import { formatTime } from "../shared/replay.js";
import { levelProgress } from "../shared/content.js";
import { settings } from "./settings.js";
import type { Profile } from "../shared/types.js";
export const tiers = [
  "",
  "Beginner",
  "Easy",
  "Intermediate",
  "Hard",
  "Expert",
  "Extreme",
];
export const medalNames = ["Unranked", "Bronze", "Silver", "Gold", "Platinum"];
export type View = {
  user: Profile;
  maps: any[];
  content: any;
  community: any;
  selected: string;
  preview?: string;
  inventoryCategory?: string;
  challengeCategory?: string;
  inventorySide?: string;
  crateResults?: any[];
  inventorySearch?: string;
  inventoryFilter?: string;
  inventorySort?: string;
  crateTier?: number;
  crateContents?: boolean;
  crateSearch?: string;
  cratePreview?: any;
  inspect?: boolean;
  revealing?: boolean;
  revealIndex?: number;
  filter: { search: string; tier: string; status: string; sort: string; short?: boolean; mode?: string };
  lobby: any;
};
const button = (label: string, action: string, extra = "", primary = false) =>
  '<button type="button" class="' +
  (primary ? "primary" : "") +
  '" data-action="' +
  action +
  '" ' +
  extra +
  ">" +
  label +
  "</button>";
const tier = (n: number) =>
  n === 0
    ? '<span class="tier">PRACTICE / UNRATED</span>'
    : '<span class="tier tier-' +
      n +
      '">TIER ' +
      n +
      " <span>" +
      tiers[n] +
      "</span></span>";
const avatar = (user: any) =>
  user.avatar
    ? '<img class="avatar" src="' + e(user.avatar) + '" alt="">'
    : '<span class="avatar frog-icon"><i></i><i></i></span>';
export function loginPage(dev: boolean, discord: boolean, admitted = false) {
  return (
    '<section class="login-v3"><h1>FROGHOPPING</h1>' +
    (admitted
      ? '<a class="button primary" href="/auth/discord">CONTINUE WITH DISCORD</a>'
      : '<form id="invite-form"><label for="invite-code">INVITE CODE</label><input id="invite-code" name="code" type="password" autocomplete="off" maxlength="128" required><button class="primary">CONTINUE</button></form><a class="returning-login" href="/auth/discord">ALREADY REGISTERED? CONTINUE WITH DISCORD</a>') +
    (new URLSearchParams(location.search).has("invite") && !admitted
      ? "<p>Enter your invite code to register.</p>"
      : "") +
    (dev
      ? '<button class="dev-login" data-action="dev">LOCAL DEVELOPMENT LOGIN</button>'
      : "") +
    "</section>"
  );
}
export function playPage(v: View) {
  const map = v.maps.find((m) => m.id === v.selected) ?? v.maps[0];
  if (!map)
    return '<section class="panel"><h1>NO COURSES AVAILABLE</h1></section>';
  return (
    '<section class="operation"><div class="operation-selection"><h1>PLAY</h1><div class="operation-map" style="background-image:url(' +
    e(map.thumbnail) +
    ')"></div><div class="section-heading"><h2>' +
    e(map.name) +
    "</h2>" +
    tier(map.ranked === false ? 0 : map.tier) +
    '</div><p class="map-author">BY ' +
    e(map.author) +
    " / " +
    (map.ranked === false ? "UNVERIFIED ZONES" : map.stages + " STAGES") +
    '</p><div class="course-facts"><span>PERSONAL BEST <b>' +
    (map.ranked === false ? "PRACTICE" : formatTime(map.pb)) +
    "</b></span><span>MASTERY <b>" +
    (map.ranked === false ? "DISABLED" : medalNames[map.medal ?? 0]) +
    '</b></span></div><div class="operation-actions">' +
    button("START RUN", "play", 'data-id="' + e(map.id) + '"', true) +
    button("CHANGE MAP", "nav", 'data-page="maps"') +
    '</div><div class="lobby-actions">' +
    button("CREATE LOBBY", "create", 'data-id="' + e(map.id) + '"') +
    '<form id="join-form"><input name="code" aria-label="Lobby code" maxlength="6" placeholder="LOBBY CODE" required><button type="submit">JOIN</button></form></div><p class="control-note">WASD MOVE · SPACE AUTO-HOP · F INSPECT · Z UNSTICK</p></div><div class="agent-stage" data-model-preview data-preview-id="' +
    e(v.user.equipped.agent) +
    '"><span>' +
    e(v.user.name) +
    "<small>" +
    "LEVEL " +
    v.user.level +
    '</small></span></div><aside class="social-rail"><h2>SERVER</h2><p>' +
    v.community.online.length +
    " ONLINE</p>" +
    v.community.online
      .map(
        (u: any) =>
          '<div class="social-player">' +
          avatar(u) +
          "<span>" +
          e(u.name) +
          "</span></div>",
      )
      .join("") +
    '<div class="section-heading"><h2>ACTIVITY</h2></div><div class="activity">' +
    v.community.activity
      .slice(0, 5)
      .map((a: any) => "<p>" + e(a.text) + "</p>")
      .join("") +
    "</div>" +
    v.community.lobbies
      .map((l: any) =>
        button(
          e(l.code) + " / " + l.players + " PLAYERS",
          "join",
          'data-code="' + e(l.code) + '"',
        ),
      )
      .join("") +
    "</aside></section>"
  );
}
export function crateCountdown(until: number, now = Date.now()) {
  const seconds = Math.max(0, Math.ceil((until - now) / 1000));
  return seconds
    ? `CRATE ${Math.floor(seconds / 60)
        .toString()
        .padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`
    : "CRATE AVAILABLE";
}
export function mapsPage(v: View) {
  const f = v.filter;
  let maps = v.maps.filter(
    (m) =>
      (!f.search ||
        (m.name + " " + m.tags.join(" "))
          .toLowerCase()
          .includes(f.search.toLowerCase())) &&
      (!f.tier || m.tier === Number(f.tier)) &&
      (!f.mode || mapMode(m) === f.mode) &&
      (!f.short || (m.typical_seconds != null && m.typical_seconds <= 120)) &&
      (!f.status ||
        (f.status === "complete"
          ? m.pb != null
          : f.status === "uncomplete"
            ? m.pb == null
            : f.status === "played"
              ? m.plays
              : f.status === "unplayed"
                ? !m.plays
                : m.favourite)),
  );
  maps.sort(
    (a, b) =>
      (f.sort === "shortest"
        ? (a.typical_seconds ?? Infinity) - (b.typical_seconds ?? Infinity)
        : f.sort === "recommended"
          ? Number(!(a.tier <= 2 && a.typical_seconds != null && a.typical_seconds <= 120)) - Number(!(b.tier <= 2 && b.typical_seconds != null && b.typical_seconds <= 120)) || a.tier-b.tier || (a.typical_seconds ?? Infinity)-(b.typical_seconds ?? Infinity)
        : f.sort === "name"
        ? a.name.localeCompare(b.name)
        : f.sort === "pb"
          ? (a.pb ?? Infinity) - (b.pb ?? Infinity)
          : f.sort === "recent"
            ? String(b.last_played ?? "").localeCompare(
                String(a.last_played ?? ""),
              )
            : f.sort === "crate"
              ? Math.max(0, (a.crateUntil ?? 0) - Date.now()) -
                Math.max(0, (b.crateUntil ?? 0) - Date.now())
              : a.tier - b.tier) || a.name.localeCompare(b.name),
  );
  const select = (name: string, values: [string, string][], current: string) =>
    '<select aria-label="' +
    name +
    '" data-filter="' +
    name +
    '">' +
    values
      .map(
        ([val, label]) =>
          '<option value="' +
          val +
          '" ' +
          (val === current ? "selected" : "") +
          ">" +
          label +
          "</option>",
      )
      .join("") +
    "</select>";
  return (
    '<section class="panel compact-maps"><div class="page-title"><div><h1>MAPS</h1></div><span class="muted">' +
    v.maps.length +
    ' COURSES / TIERS 1–6</span></div><div class="filters"><input id="map-search" aria-label="Search maps" placeholder="Search maps or tags…" value="' +
    e(f.search) +
    '">' +
    select(
      "tier",
      [
        ["", "ALL TIERS"],
        ...tiers
          .slice(1)
          .map(
            (label, i) =>
              [String(i + 1), "TIER " + (i + 1) + " / " + label] as [
                string,
                string,
              ],
          ),
      ],
      f.tier,
    ) +
    select(
      "status",
      [
        ["", "ALL MAPS"],
        ["complete", "COMPLETED"],
        ["uncomplete", "UNCOMPLETED"],
        ["played", "PLAYED"],
        ["unplayed", "UNPLAYED"],
        ["favourite", "FAVOURITES"],
      ],
      f.status,
    ) +
    select(
      "sort",
      [
        ["recommended", "RECOMMENDED"],
        ["shortest", "SHORTEST RECORDED RUNS"],
        ["tier", "DIFFICULTY"],
        ["name", "NAME"],
        ["pb", "PB"],
        ["recent", "RECENT"],
        ["crate", "CRATE AVAILABLE"],
      ],
      f.sort,
    ) +
    '</div><div class="map-quick-filters" role="group" aria-label="Quick map filters">' +
    `<button data-action="map-filter-clear" class="${!f.tier && !f.status && !f.search && !f.mode && !f.short ? "active" : ""}">All</button>` +
    ["bhop","surf","kz"].map(mode=>`<button data-action="map-filter-mode" data-id="${mode}" aria-pressed="${f.mode === mode}" class="${f.mode === mode ? "active" : ""}">${mode === "bhop" ? "Bhop" : mode === "surf" ? "Surf" : "KZ"}</button>`).join("") +
    `<button data-action="map-filter-favourites" aria-pressed="${f.status === "favourite"}" class="${f.status === "favourite" ? "active" : ""}">&#9733; Favourites</button>` +
    `<button data-action="map-filter-short" aria-pressed="${!!f.short}" class="${f.short ? "active" : ""}">Shorter maps</button>` +
    [1,2,3,4,5,6].map(t=>`<button data-action="map-filter-tier" data-id="${t}" aria-pressed="${f.tier === String(t)}" class="${f.tier === String(t) ? "active" : ""}">Tier ${t}</button>`).join("") +
    `<span class="muted">${maps.length} shown</span></div><div class="map-grid">` +
    maps
      .map(
        (m) => `<article class="course-card">
      <div class="course-preview"><img src="${e(m.thumbnail)}" alt="${e(m.name)} preview" loading="lazy"><span class="course-badge">${mapMode(m).toUpperCase()} · TIER ${m.tier}</span><button data-action="favourite" data-id="${e(m.id)}" aria-label="Favourite ${e(m.name)}" aria-pressed="${!!m.favourite}" class="course-favourite ${m.favourite ? "active" : ""}">${m.favourite ? "★" : "☆"}</button></div>
      <div class="course-card-body"><h2>${e(m.name)}</h2><p class="muted">${m.typical_seconds != null ? `Median finish ${formatTime(m.typical_seconds)}` : "Length not yet measured"} · ${m.stages ?? 1} stages</p><div class="course-stats"><div><small>PERSONAL BEST</small><b>${formatTime(m.pb)}</b></div><div><small>RECORD</small><b>${formatTime(m.record)}</b></div></div>
      <div class="course-card-status"><span>${m.pb != null ? "✓ COMPLETED" : tiers[m.tier]}</span><span class="crate-availability" data-crate-until="${m.crateUntil ?? 0}">${crateCountdown(m.crateUntil ?? 0)}</span></div>
      <div class="course-card-actions"><button data-action="board" data-id="${e(m.id)}">TIMES</button><button class="primary" data-action="play" data-id="${e(m.id)}">PLAY</button></div></div></article>`,
      )
      .join("") +
    "</div>" +
    (maps.length
      ? ""
      : '<div class="empty">No maps match these filters.</div>') +
    "</section>"
  );
}
export function challengesPage(v: View) {
  const challenges = v.content.challenges;
  return `<section class="panel compact-challenges"><div class="page-title"><h1>Challenges</h1></div><p class="muted">Hourly rewards reset on the hour; daily rewards reset at 00:00 UTC. Crates are awarded automatically. Time challenges accept any tier and can be completed together; tier challenges require the stated tier.</p>${[
    "hourly",
    "daily",
    "weekly",
    "permanent",
  ]
    .map((period) => {
      const rows = challenges.filter(
        (c: any) => (c.period ?? "permanent") === period,
      );
      if (!rows.length) return "";
      return `<h2 class="list-heading">${period === "permanent" ? "Milestones" : period === "daily" ? "Daily" : period === "hourly" ? "Hourly" : "Weekly"}</h2><div class="challenge-list">${rows
        .map((c: any) => {
          const item = v.content.cosmetics.find(
            (i: any) => i.id === c.cosmetic,
          );
          return `<article class="challenge-row ${c.rewarded ? "complete" : ""}"><span class="challenge-check">${c.rewarded ? "✓" : "·"}</span><div class="challenge-description"><h3>${e(c.name)}</h3><p>${e(c.description)}</p></div><span class="challenge-count">${Math.min(c.target, Math.floor(c.progress))} / ${c.target}</span><div class="challenge-reward">${item ? `<button data-action="challenge-preview" data-id="${e(item.id)}" title="Inspect ${e(item.name)}"><img src="${e(item.image)}" alt=""><span>${e(item.name)}</span></button>` : ""}${c.xp ? `<small>+${c.xp} XP</small>` : ""}${c.crateTier ? `<small>Tier ${c.crateTier} case</small>` : ""}</div></article>`;
        })
        .join("")}</div>`;
    })
    .join("")}</section>`;
}
export { inventoryPage } from "./inventory.js";
export function leaderboardPage(v: View, rows: any[], metric: string) {
  if (!v.maps.length)
    return '<section class="panel"><h1>LEADERBOARDS</h1><div class="empty">No courses available.</div></section>';
  const map = v.maps.find((m) => m.id === v.selected) ?? v.maps[0];
  const options = [
    ["level", "LEVEL"],
    ["finishes", "TOTAL FINISHES"],
    ["unique", "UNIQUE MAPS"],
    ["records", "RECORDS"],
    ["playtime", "PLAYTIME"],
    ["challenges", "CHALLENGES"],
  ];
  const members = [...v.community.members].sort(
    (a, b) =>
      (metric === "level"
        ? b.level
        : metric === "records"
          ? b.records
          : (b.stats[metric] ?? 0)) -
      (metric === "level"
        ? a.level
        : metric === "records"
          ? a.records
          : (a.stats[metric] ?? 0)),
  );
  return (
    '<section class="panel"><div class="page-title"><div><div class="eyebrow">VERIFIED RUNS</div><h1>LEADERBOARDS</h1></div><select id="board-map" aria-label="Leaderboard map">' +
    v.maps
      .map(
        (m) =>
          '<option value="' +
          e(m.id) +
          '" ' +
          (m.id === map.id ? "selected" : "") +
          ">" +
          e(m.name) +
          (m.ranked === false ? " / PRACTICE" : " / T" + m.tier) +
          "</option>",
      )
      .join("") +
    '</select></div><div class="board-layout"><div><div class="section-heading"><h2>' +
    e(map.name) +
    "</h2>" +
    tier(map.ranked === false ? 0 : map.tier) +
    '</div><div class="table-wrap"><table><thead><tr><th>#</th><th>PLAYER</th><th>TIME</th><th>SET</th><th>REPLAY</th></tr></thead><tbody>' +
    rows
      .map(
        (r, i) =>
          '<tr class="' +
          (r.user_id === v.user.id ? "you" : "") +
          '"><td class="' +
          (i === 0 ? "gold" : "") +
          '">' +
          String(i + 1).padStart(2, "0") +
          '</td><td><button class="text-button" data-action="profile" data-id="' +
          e(r.user_id) +
          '">' +
          e(r.name) +
          (r.user_id === v.user.id ? " <small>YOU</small>" : "") +
          '</button></td><td class="time">' +
          formatTime(r.seconds) +
          "</td><td>" +
          new Date(r.created_at).toLocaleDateString() +
          "</td><td>" +
          button(
            "â–¶",
            "replay",
            'data-id="' + e(r.run_id) + '" aria-label="Watch replay"',
          ) +
          button(
            "â—Œ",
            "ghost",
            'data-id="' + e(r.run_id) + '" aria-label="Race ghost"',
          ) +
          "</td></tr>",
      )
      .join("") +
    "</tbody></table>" +
    (rows.length ? "" : '<div class="empty">No times yet.</div>') +
    '</div><p class="page-note">100-tick timing. Every ranked run is reproduced and verified by the server.</p></div><aside><div class="section-heading"><h2>Players</h2><select id="member-metric" aria-label="Member ranking">' +
    options
      .map(
        ([val, label]) =>
          '<option value="' +
          val +
          '" ' +
          (metric === val ? "selected" : "") +
          ">" +
          label +
          "</option>",
      )
      .join("") +
    "</select></div>" +
    members
      .map(
        (u, i) =>
          '<div class="member-row"><span>' +
          String(i + 1).padStart(2, "0") +
          "</span>" +
          button(e(u.name), "profile", 'data-id="' + e(u.id) + '"') +
          "<b>" +
          (metric === "level"
            ? u.level
            : metric === "records"
              ? u.records
              : metric === "playtime"
                ? ((u.stats.playtime ?? 0) / 3600).toFixed(1) + "h"
                : (u.stats[metric] ?? 0)) +
          "</b></div>",
      )
      .join("") +
    "</aside></div></section>"
  );
}
export function usersPage(data: any, search: string) {
  return `<section class="panel users-page"><div class="page-title"><h1>USERS</h1><small>${data.total} ${data.total === 1 ? "PLAYER" : "PLAYERS"}</small></div>
    <div class="filters"><input id="users-search" type="search" aria-label="Search users" placeholder="Search names" value="${e(search)}" maxlength="80"></div>
    <div class="users-list">${data.users.map((u: any) => `<button class="user-row" data-action="profile" data-id="${e(u.id)}">${avatar(u)}<strong>${e(u.name)}</strong><span>Level ${u.level}</span><span class="user-joined">Joined ${new Date(u.created_at).toLocaleDateString()}</span><span aria-hidden="true">â†’</span></button>`).join("") || '<p class="muted">No players found.</p>'}</div>
    <div class="users-pagination"><button data-action="users-page" data-id="${data.page - 1}" ${data.page <= 1 ? "disabled" : ""}>PREVIOUS</button><small>Page ${data.page} / ${Math.max(1, Math.ceil(data.total / data.pageSize))}</small><button data-action="users-page" data-id="${data.page + 1}" ${data.page * data.pageSize >= data.total ? "disabled" : ""}>NEXT</button></div></section>`;
}

export function profilePage(v: View, data: any) {
  const u = data.user,
    progress = levelProgress(u.xp),
    st = u.stats;
  const stats: [[string, string | number], ...[string, string | number][]] = [
    ["Maps", st.unique ?? 0],
    ["Finishes", st.finishes ?? 0],
    ["Playtime", ((st.playtime ?? 0) / 3600).toFixed(1) + "h"],
  ];
  if (st.maxSpeed) stats.push(["Top speed", Math.round(st.maxSpeed) + " u/s"]);
  if (st.records) stats.push(["Records", st.records]);
  if (st.challenges) stats.push(["Challenges", st.challenges]);
  if (st.pbs) stats.push(["Personal bests", st.pbs]);
  const section = (title: string, body: string) =>
    body
      ? `<section class="profile-section"><h2>${title}</h2>${body}</section>`
      : "";
  return `<section class="panel compact-profile"><div class="profile-identity">${avatar(u)}<div><h1>${e(u.name)}</h1><p>Level ${u.level} <span>· Joined ${new Date(u.created_at).toLocaleDateString()}</span></p><div class="profile-xp"><progress value="${progress.current}" max="${progress.needed}"></progress><small>${progress.current} / ${progress.needed} XP</small></div></div></div><div class="profile-stats">${stats.map(([label, value]) => `<span><b>${value}</b> ${label}</span>`).join("")}</div><div class="profile-layout"><div>${section(
    "Achievements",
    `<div class="compact-achievements">${v.content.achievements
      .map((a: any) => {
        const won = data.achievements.includes(a.id);
        return `<div class="achievement-line ${won ? "complete" : ""}"><span>${won ? "✓" : "◇"}</span><div><b>${e(a.hidden && !won ? "???" : a.name)}</b><p>${e(a.hidden && !won ? "Hidden achievement" : a.description)}</p></div></div>`;
      })
      .join("")}</div>`,
  )}${section("Map mastery", data.mastery.map((m: any) => `<div class="compact-row"><span>${e(m.name)}</span><b>${medalNames[m.medal]}</b></div>`).join(""))}${section(
    "Recent activity",
    data.activity
      .slice(0, 5)
      .map((a: any) => `<p class="activity-line">${e(a.text)}</p>`)
      .join(""),
  )}</div><aside>${section(
    "Equipped",
    `<div class="equipped-items">${["agent", "knife", "gloves"]
      .map((slot) => {
        const item = [...v.content.cosmetics, ...(u.inventory ?? [])].find(
          (c: any) => c.id === u.equipped[slot],
        );
        return item
          ? `<button data-action="challenge-preview" data-id="${e(item.id)}"><img ${item.templateId ? `data-thumbnail-id="${e(item.id)}"` : `src="${e(item.image)}"`} alt=""><span>${e(item.name + (item.finishName && item.finish && item.finish !== "default" ? " | " + item.finishName : ""))}</span><small>${slot === "agent" ? "Agent / playermodel" : slot}</small></button>`
          : "";
      })
      .join("")}</div>`,
  )}${section(
    "Most played",
    data.played
      .slice(0, 5)
      .map(
        (p: any) =>
          `<div class="compact-row"><span>${e(p.name)}</span><small>${p.plays} ${p.plays === 1 ? "attempt" : "attempts"}</small></div>`,
      )
      .join(""),
  )}${section("Favourites", data.favourites?.map((f: any) => `<p>${e(f.name)}</p>`).join("") ?? "")}${section("Titles", data.titles?.map((t: string) => `<p>${e(t)}</p>`).join("") ?? "")}</aside></div></section>`;
}
export function settingsPage(admin: boolean) {
  const range = (
    key: keyof typeof settings,
    label: string,
    min: number,
    max: number,
    step: number,
  ) =>
    `<label class="setting"><span>${label}</span><input name="${key}" type="range" min="${min}" max="${max}" step="${step}" value="${settings[key]}"><output>${settings[key]}</output></label>`;
  const toggle = (key: keyof typeof settings, label: string) =>
    `<label class="setting"><span>${label}</span><input name="${key}" type="checkbox" ${settings[key] ? "checked" : ""}></label>`;
  const select = (
    key: keyof typeof settings,
    label: string,
    options: [string, string][],
  ) =>
    `<label class="setting"><span>${label}</span><select name="${key}">${options.map(([val, label]) => `<option value="${val}" ${String(settings[key]) === val ? "selected" : ""}>${label}</option>`).join("")}</select></label>`;
  const names: Record<string, string> = {
    duck: "Crouch",
    draw: "Draw / equip",
    stage: "Reset stage",
    unstick: "Unstick",
  };
  const keyName = (key: string) =>
    key
      .replace("Key", "")
      .replace("ControlLeft", "Left Ctrl")
      .replace("ShiftLeft", "Left Shift");
  return `<section class="panel compact-settings"><div class="page-title"><h1>Settings</h1><small>Saved automatically</small></div><form id="settings-form"><div class="settings-columns"><fieldset><legend>Game</legend>${range("fov", "World FOV", 70, 120, 1)}${range("viewmodelFov", "Viewmodel FOV", 50, 100, 1)}${range("sensitivity", "Mouse sensitivity", 0.1, 5, 0.05)}${toggle("rawInput", "Raw mouse input")}${toggle("autoBhop", "Auto bhop")}${toggle("velocity", "Show speed")}${toggle("timer", "Show timer")}${toggle("pbDelta", "Show PB delta")}${toggle("otherPlayers", "Show other players")}${toggle("ghosts", "Show ghost")}${select(
    "viewmodelBob",
    "Viewmodel bob",
    [
      ["off", "Off"],
      ["low", "Low"],
      ["normal", "Normal"],
    ],
  )}</fieldset><fieldset><legend>Video</legend>${range("renderScale", "Render scale", 0.5, 1.5, 0.1)}${select(
    "fpsLimit",
    "FPS limit",
    [
      ["0", "Unlimited"],
      ["60", "60"],
      ["100", "100"],
      ["120", "120"],
      ["144", "144"],
      ["165", "165"],
      ["240", "240"],
    ],
  )}${toggle("antialias", "Anti-aliasing (reload)")}${button("Fullscreen", "fullscreen")}<p class="settings-note">Resolution follows the window. Display sync is managed by your browser.</p><div class="settings-audio"><h2>Audio</h2>${range("master", "Master volume", 0, 1, 0.05)}${range("game", "Game", 0, 1, 0.05)}${range("ui", "Interface", 0, 1, 0.05)}${range("ambience", "Ambience", 0, 1, 0.05)}</div></fieldset><fieldset><legend>Controls</legend><p class="settings-note">Click an action, then press a key to add it. Press an existing key to remove it. Escape cancels. Mouse wheel jumps.</p>${Object.entries(
    settings.binds,
  )
    .map(
      ([action, keys]) =>
        `<label class="setting"><span>${names[action] ?? action}</span><button type="button" data-action="bind" data-bind="${action}">${keys.map(keyName).map(e).join(" / ") || "Unbound"}</button></label>`,
    )
    .join(
      "",
    )}<details class="diagnostics"><summary>Diagnostics</summary>${button("Performance overlay", "debug")}${admin ? button("Admin noclip", "noclip") : ""}</details></fieldset></div></form></section>`;
}
export function adminPage(status: any, v: View) {
  return (
    '<section class="panel narrow"><div class="page-title"><div><div class="eyebrow">SERVER ADMINISTRATION</div><h1>SERVER</h1></div></div><p class="page-note">' +
    status.users +
    " accounts · " +
    status.runs +
    " runs · " +
    Math.floor(status.uptime / 60) +
    " minutes uptime</p>" +
    '<div class="section-heading"><h2>GRANT / REVOKE REWARDS</h2></div><form id="grant-form" class="admin-form"><input name="userId" placeholder="Player Discord ID" required><select name="action"><option value="achievement">Achievement</option></select><input name="id" placeholder="Item ID" required><select name="enabled"><option value="true">Grant</option><option value="false">Revoke</option></select><button>APPLY</button></form><div class="section-heading"><h2>INVALIDATE RUN</h2></div><form id="invalidate-form" class="inline-form"><input name="id" placeholder="Run UUID from replay" required><button>INVALIDATE</button></form><div class="section-heading"><h2>MAP METADATA</h2></div><form id="map-admin-form" class="admin-form"><select name="id">' +
    status.maps.map((m: any) => "<option>" + e(m.id) + "</option>").join("") +
    '</select><input name="name" placeholder="Display name" required><input name="description" placeholder="Description" required><input type="number" name="tier" min="1" max="6" value="1" required><select name="enabled"><option value="true">Enabled</option><option value="false">Disabled</option></select><button>SAVE MAP</button></form><div class="section-heading"><h2>SPECIAL CHALLENGE</h2></div><form id="challenge-admin-form" class="admin-form"><input name="id" placeholder="unique-challenge-id" pattern="[a-z0-9-]+" required><input name="name" placeholder="Challenge name" required><input name="description" placeholder="Description" required><select name="period"><option>permanent</option><option>daily</option><option>weekly</option></select><select name="metric">' +
    [
      "finishes",
      "unique",
      "pbs",
      "tier2",
      "tier4",
      "tier5",
      "tier6",
      "maxSpeed",
      "playtime",
      "multiplayer",
      "clean",
      "records",
    ]
      .map((x) => "<option>" + x + "</option>")
      .join("") +
    '</select><input name="target" type="number" min="1" placeholder="Target" required><input name="xp" type="number" min="0" max="10000" placeholder="XP reward" required><button>CREATE CHALLENGE</button></form></section>'
  );
}
